/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  V3_CONTENT_CARD_GRADIENTS,
  V3ContentCardAction,
  V3ContentCardItem,
} from '@app/app/shared/components-v3/content-card/v3-content-card.model';
import { PlaylistCardShort } from '@app/app/shared/models';
import { LucideBookOpen, LucideCalendar, LucideCheck, LucideIconInput, LucideLayers, LucideLock } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';

export function mapPlaylistCardToContentCardItem(
  card: PlaylistCardShort,
  cardIndex: number,
  transloco: TranslocoService,
): V3ContentCardItem {
  const progressPercentage = playlistCardProgressPercentage(card);
  const inProgress = !card.completed && (card.started || progressPercentage > 0);
  const isLtiCourse = isLtiCourseCard(card);
  const showProgress = !isLtiCourse && !isEventCard(card) && inProgress;
  const gradeLabel =
    isLtiCourse && (inProgress || card.completed)
      ? transloco.translate('translations.cardItem.grade', { value: progressPercentage })
      : null;
  return {
    uid: card._id,
    title: card.header?.trim() || playlistCardUntitledLabel(card, transloco),
    titleIsPlaceholder: !card.header?.trim(),
    thumbnail: card.image || null,
    gradient: V3_CONTENT_CARD_GRADIENTS[cardIndex % V3_CONTENT_CARD_GRADIENTS.length],
    coverIcon: !isEventCard(card) && !card.completed && !inProgress ? LucideLock : null,
    typeLabel: playlistCardTypeLabel(card, transloco),
    typeIcon: playlistCardTypeIcon(card),
    progress: showProgress ? progressPercentage : null,
    gradeLabel,
    gradePassing: progressPercentage >= 60,
    statusBadge: card.completed
      ? { label: transloco.translate('translations.cardItem.completed'), tone: 'success', icon: LucideCheck }
      : null,
  };
}

export function mapPlaylistCardToContentCardAction(card: PlaylistCardShort, transloco: TranslocoService): V3ContentCardAction | null {
  if (isEventCard(card)) {
    return { label: transloco.translate('translations.eventLearnerView.register'), type: 'primary' };
  }
  if (card.completed) {
    return null;
  }
  const hasProgress = playlistCardProgressPercentage(card) > 0;
  return card.started || hasProgress
    ? { label: transloco.translate('translations.cardItem.continue'), type: 'primary' }
    : { label: transloco.translate('translations.cardItem.start'), type: 'primary' };
}

export function openPlaylistCard(card: PlaylistCardShort, emit: (card: PlaylistCardShort) => void): void {
  emit(card);
}

function playlistCardUntitledLabel(card: PlaylistCardShort, transloco: TranslocoService): string {
  const type = (card?.v3CardType || card?.collectionType)?.toUpperCase();
  switch (type) {
    case 'COURSE':
    case 'LTI_COURSE':
      return transloco.translate('translations.cardItem.untitled.course');
    case 'PROGRAM':
    case 'MODULE':
      return transloco.translate('translations.cardItem.untitled.programme');
    case 'EVENT':
      return transloco.translate('translations.cardItem.untitled.event');
    case 'RESOURCE':
      return transloco.translate('translations.cardItem.untitled.resource');
    case 'ASSESSMENT':
      return transloco.translate('translations.cardItem.untitled.assessment');
    default:
      return transloco.translate('translations.cardItem.untitled.card');
  }
}

function playlistCardTypeLabel(card: PlaylistCardShort, transloco: TranslocoService): string {
  const type = card?.v3CardType || card?.collectionType;
  if (!type) {
    return '';
  }
  if (type.toUpperCase() === 'LTI_COURSE') {
    if (card?.lmsPlatformType === 'CANVAS') {
      return transloco.translate('translations.cardItem.type.CANVAS_COURSE');
    }
    return transloco.translate('translations.cardItem.type.LTI_COURSE');
  }
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
}

function playlistCardTypeIcon(card: PlaylistCardShort): LucideIconInput | null {
  const type = (card?.v3CardType || card?.collectionType)?.toUpperCase();
  if (!type) {
    return null;
  }
  if (type === 'EVENT') {
    return LucideCalendar;
  }
  if (type === 'MODULE' || type === 'PROGRAM') {
    return LucideLayers;
  }
  return LucideBookOpen;
}

function isLtiCourseCard(card: PlaylistCardShort): boolean {
  return (card?.v3CardType || card?.collectionType)?.toUpperCase() === 'LTI_COURSE';
}

function isEventCard(card: PlaylistCardShort): boolean {
  return (card?.v3CardType || card?.collectionType || card?.type)?.toUpperCase() === 'EVENT';
}

function playlistCardProgressPercentage(card: PlaylistCardShort): number {
  const completed = card?.completedItemsCount ?? 0;
  const total = card?.totalItemsCount ?? 0;
  if (total > 0) {
    return Math.round((completed / total) * 100);
  }
  return card?.completed ? 100 : 0;
}
